import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { ABSOLUTE_URL_REGEX, isLikelyUrl } from 'client/utils/helpers/UrlUtils/UrlsUtils';
import { ENHANCED_MEASURES } from 'client/utils/constants/enhancedMeasures';

const getAssetId = (asset) => asset?.dataproviderassigneduniqueid || asset?.id;

const containsUrl = (value) => {
    if (typeof value !== 'string') {
        return false;
    }

    const trimmed = value.trim();
    if (!trimmed) {
        return false;
    }

    if (isLikelyUrl(trimmed)) {
        return true;
    }

    return new RegExp(ABSOLUTE_URL_REGEX).test(trimmed);
};

const hasLinkedFilesForGuidance = (guidance) => Array.isArray(guidance?.linkedFiles) && guidance.linkedFiles.length > 0;

const linkedFileHasUrl = (linkedFile) => {
    return containsUrl(linkedFile?.fullpath) || containsUrl(linkedFile?.url) || containsUrl(linkedFile?.path);
};

const guidanceHasUrl = (guidance) => {
    return containsUrl(guidance?.description) || containsUrl(guidance?.additionalInformation);
};

const hasMatchingCodelist = (item, variant) => {
    const variantCodelist = ENHANCED_MEASURES[variant]?.bespoke;
    if (!variantCodelist) {
        return false;
    }
    return item.name === variantCodelist;
};

export const normaliseSystemIds = (list) => {
    if (!Array.isArray(list)) {
        return [];
    }

    return list
        .map(item => item.systemId ?? '')
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
};

const normaliseGuidanceSignature = (guidances) => {
    if (!Array.isArray(guidances)) {
        return [];
    }

    return guidances
        .map(guidance => ({
            name: guidance?.name ?? '',
            description: guidance?.description ?? '',
            additionalInformation: guidance?.additionalInformation ?? '',
            linkedFileSystemIds: normaliseSystemIds(guidance?.linkedFiles)
        }))
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
};

/**
 * Groups assets by identical linkedFiles + guidances.
 * Useful for list-mode: if there is more than 1 group, assets differ.
 *
 * @param {Array} assets
 * @returns {{
 *   linkedFilesAndGuidances: Array<{linkedFiles: Array, guidances: Array, assets: Array}>,
 *   hasMultipleGuidances: boolean,
 *   shouldShowContactButton: boolean,
 *   isPending: boolean,
 *   error: boolean
 * }}
 */
export const useLinkedFiles = (assets = [], variant) => {
    const supplementaryData = useSelector(state => state?.supplementaryData?.data || {});
    const assetErrors = useSelector(state => state?.supplementaryData?.assetErrors || {});
    const assetIds = assets.map(getAssetId).filter(Boolean);
    // Scoped to just the assets rendered in this box, not the legacy global supplementaryData.error
    // flag (which is only ever set by the bulk enhanced-measures retry path)
    const error = assetIds.some(id => assetErrors[id]);
    const isPending = !error && assetIds.some(id => !supplementaryData[id]);

    const grouped = [];
    let hasLinkedFiles = false;
    let hasUrl = false;

    assets.forEach((asset) => {
        const assetId = getAssetId(asset);
        if (!assetId) {
            return;
        }

        const suppData = supplementaryData[assetId];
        if (!suppData) {
            return;
        }

        const linkedFiles = (Array.isArray(suppData.linkedFiles) ? suppData.linkedFiles : [])
            .filter(linkedFile => hasMatchingCodelist(linkedFile, variant));

        const guidances = (Array.isArray(suppData.guidances) ? suppData.guidances : [])
            .filter(guidance => hasMatchingCodelist(guidance, variant));

        const hasGuidanceLinkedFiles = guidances.some(hasLinkedFilesForGuidance);

        hasLinkedFiles = hasLinkedFiles || linkedFiles.length > 0 || hasGuidanceLinkedFiles;
        hasUrl = hasUrl ||
            linkedFiles.some(linkedFileHasUrl) ||
            guidances.some(guidance => guidanceHasUrl(guidance) || (Array.isArray(guidance?.linkedFiles) && guidance.linkedFiles.some(linkedFileHasUrl)));

        const signature = {
            linkedFiles: normaliseSystemIds(linkedFiles),
            guidances: normaliseGuidanceSignature(guidances)
        };

        const existingGroup = grouped.find(group => isEqual(group.signature, signature));
        if (existingGroup) {
            existingGroup.assets.push(asset);
            return;
        }

        grouped.push({
            signature,
            linkedFiles,
            guidances,
            assets: [asset]
        });
    });

    const hasMultipleGuidances = grouped.length > 1;
    const linkedFilesAndGuidances = grouped.map(({ signature, ...rest }) => rest);
    const shouldShowContactButton = !hasLinkedFiles && !hasUrl;

    return { linkedFilesAndGuidances, hasMultipleGuidances, shouldShowContactButton, isPending, error };
};
